import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Container,
  useToast,
  HStack,
  Avatar,
  IconButton
} from '@chakra-ui/react';
import { FiArrowLeft, FiUser, FiSettings, FiLock } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../utils/firebaseUtils';
import ChangePassword from './ChangePassword';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  // Note: company is read-only for mobile users
}

const Profile: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { user, userProfile, userCompanies, refreshUserProfile } = useAuth();
  console.log({userProfile}, {user})
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: userProfile?.firstName || '',
      lastName: userProfile?.lastName || '',
      email: userProfile?.email || user?.email || '',
      phone: userProfile?.phone || ''
      // company is read-only, not included in form data
    }
  });

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    try {
      if (!user || !user.uid) {
        throw new Error("User not found.");
      }
      
      // Combine firstName and lastName to create the name field
      const combinedName = `${data.firstName.trim()} ${data.lastName.trim()}`.trim();
      
      await updateUserProfile(user.uid, {
        firstName: data.firstName,
        lastName: data.lastName,
        name: combinedName, // Add the combined name field
        email: data.email,
        phone: data.phone
      });
      await refreshUserProfile();
      toast({
        title: "Profile updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Error updating profile.",
        description: error.message || "An error occurred.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" py={{ base: 6, md: 8 }} w="100%" overflowX="hidden">
      <Container maxW="2xl" px={{ base: 4, md: 6 }} w="100%">
        <VStack spacing={{ base: 8, md: 10 }} w="100%">
          {/* Header */}
          <Box
            w="100%"
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <VStack spacing={6} textAlign="center" w="100%">
              <HStack w="100%" justify="space-between" align="center">
                <IconButton
                  aria-label="Go back"
                  icon={<FiArrowLeft />}
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  size="lg"
                  borderRadius="xl"
                  _hover={{
                    bg: "blue.50",
                    transform: "translateY(-1px)"
                  }}
                  transition="all 0.2s"
                />
                <Box
                  p={3}
                  borderRadius="xl"
                  bgGradient="linear(135deg, purple.400, pink.500)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 6px 24px rgba(168, 85, 247, 0.3)"
                >
                  <FiUser size="24px" color="white" />
                </Box>
                <Box w="44px" /> {/* Spacer for center alignment */}
              </HStack>
              
              <VStack spacing={4}>
                <Box position="relative">
                  <Avatar
                    size={{ base: "xl", md: "2xl" }}
                    name={userProfile ? `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() : (user?.displayName || 'User')}
                    src={user?.photoURL || undefined}
                    border="4px solid"
                    borderColor="white"
                    boxShadow="0 8px 32px rgba(0,0,0,0.15)"
                  />
                  <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    p={2}
                    borderRadius="full"
                    bgGradient="linear(135deg, green.400, green.600)"
                    border="3px solid white"
                    boxShadow="0 4px 16px rgba(34, 197, 94, 0.3)"
                  >
                    <Text fontSize="sm" color="white">✓</Text>
                  </Box>
                </Box>
                
                <VStack spacing={2}>
                  <Text 
                    fontSize={{ base: "2xl", md: "3xl" }} 
                    fontWeight="800"
                    bgGradient="linear(135deg, purple.600, pink.600)"
                    bgClip="text"
                  >
                    Profile Settings
                  </Text>
                  
                  <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" maxW="md" fontWeight="500">
                    Update your personal information and account settings
                  </Text>
                </VStack>
              </VStack>
            </VStack>
          </Box>

          {/* Profile Form */}
          <Box
            w="100%" 
            maxW="md"
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <VStack spacing={6} mb={6}>
              <HStack spacing={3}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bgGradient="linear(135deg, blue.400, blue.600)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiSettings size="20px" color="white" />
                </Box>
                <Text 
                  fontSize="lg" 
                  fontWeight="700"
                  bgGradient="linear(135deg, gray.800, gray.600)"
                  bgClip="text"
                >
                  Personal Information
                </Text>
              </HStack>
            </VStack>

            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={6} w="100%">
                {/* First Name and Last Name in a responsive layout */}
                <HStack spacing={4} w="100%" align="start">
                  <FormControl isInvalid={!!errors.firstName} flex={1}>
                    <FormLabel 
                      fontSize="sm" 
                      fontWeight="700" 
                      color="gray.700"
                      mb={3}
                    >
                      👤 First Name
                    </FormLabel>
                    <Input
                      type="text"
                      placeholder="First name"
                      bg="white"
                      borderRadius="xl"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
                      }}
                      _hover={{
                        borderColor: "gray.300"
                      }}
                      fontWeight="500"
                      {...register('firstName', {
                        required: 'First name is required',
                        minLength: {
                          value: 2,
                          message: 'First name must be at least 2 characters'
                        },
                        pattern: {
                          value: /^[a-zA-Z\s]+$/,
                          message: 'First name can only contain letters and spaces'
                        }
                      })}
                    />
                    {errors.firstName && (
                      <Text color="red.500" fontSize="sm" mt={2} fontWeight="500">
                        {errors.firstName.message}
                      </Text>
                    )}
                  </FormControl>
                  
                  <FormControl isInvalid={!!errors.lastName} flex={1}>
                    <FormLabel 
                      fontSize="sm" 
                      fontWeight="700" 
                      color="gray.700"
                      mb={3}
                    >
                      👤 Last Name
                    </FormLabel>
                    <Input
                      type="text"
                      placeholder="Last name"
                      bg="white"
                      borderRadius="xl"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
                      }}
                      _hover={{
                        borderColor: "gray.300"
                      }}
                      fontWeight="500"
                      {...register('lastName', {
                        required: 'Last name is required',
                        minLength: {
                          value: 2,
                          message: 'Last name must be at least 2 characters'
                        },
                        pattern: {
                          value: /^[a-zA-Z\s]+$/,
                          message: 'Last name can only contain letters and spaces'
                        }
                      })}
                    />
                    {errors.lastName && (
                      <Text color="red.500" fontSize="sm" mt={2} fontWeight="500">
                        {errors.lastName.message}
                      </Text>
                    )}
                  </FormControl>
                </HStack>

                <FormControl isInvalid={!!errors.email}>
                  <FormLabel 
                    fontSize="sm" 
                    fontWeight="700" 
                    color="gray.700"
                    mb={3}
                  >
                    📧 Email Address
                  </FormLabel>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    bg="white"
                    borderRadius="xl"
                    border="2px solid"
                    borderColor="gray.200"
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
                    }}
                    _hover={{
                      borderColor: "gray.300"
                    }}
                    fontWeight="500"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                  {errors.email && (
                    <Text color="red.500" fontSize="sm" mt={2} fontWeight="500">
                      {errors.email.message}
                    </Text>
                  )}
                </FormControl>
                
                <FormControl>
                  <FormLabel 
                    fontSize="sm" 
                    fontWeight="700" 
                    color="gray.700"
                    mb={3}
                  >
                    📱 Phone Number
                  </FormLabel>
                  <Input
                    type="text"
                    placeholder="Enter your phone number"
                    bg="white"
                    borderRadius="xl"
                    border="2px solid"
                    borderColor="gray.200"
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
                    }}
                    _hover={{
                      borderColor: "gray.300"
                    }}
                    fontWeight="500"
                    {...register('phone', {
                      pattern: {
                        value: /^[\d\s\-\+\(\)]+$/,
                        message: 'Please enter a valid phone number'
                      }
                    })}
                  />
                  {errors.phone && (
                    <Text color="red.500" fontSize="sm" mt={2} fontWeight="500">
                      {errors.phone.message}
                    </Text>
                  )}
                </FormControl>

                {/* Company and Role Table - replaces read-only company input */}
                <Box w="100%" pt={2}>
                  <HStack spacing={3} mb={4}>
                    <Box
                      p={2}
                      borderRadius="lg"
                      bgGradient="linear(135deg, purple.400, purple.600)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <FiSettings size="16px" color="white" />
                    </Box>
                    <Text 
                      fontSize="sm" 
                      fontWeight="700" 
                      color="gray.700"
                    >
                      🏢 Company and Role
                    </Text>
                  </HStack>
                  <Box 
                    overflowX="auto"
                    bg="gray.50"
                    borderRadius="xl"
                    p={4}
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <tbody>
                        {userCompanies && userCompanies.length > 0 ? (
                          userCompanies.map((c: any) => (
                            <tr key={c.companyId}>
                              <td style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 12px', fontWeight: 600, color: '#374151' }}>
                                {c.companyName || c.companyId}
                              </td>
                              <td style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 12px', fontWeight: 500, textTransform: 'capitalize', color: '#6b7280' }}>
                                {c.role}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2} style={{ padding: '8px 12px', color: '#9ca3af', fontStyle: 'italic', textAlign: 'center' }}>
                              No company assignments
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>

                <HStack spacing={4} w="100%" pt={6}>
                  <Button
                    variant="outline"
                    size={{ base: "lg", md: "xl" }}
                    flex={1}
                    onClick={() => navigate('/dashboard')}
                    borderRadius="xl"
                    borderWidth="2px"
                    fontWeight="600"
                    _hover={{
                      bg: "gray.50",
                      transform: "translateY(-1px)"
                    }}
                    transition="all 0.2s"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size={{ base: "lg", md: "xl" }}
                    flex={1}
                    isLoading={isLoading}
                    loadingText="Updating..."
                    bgGradient="linear(135deg, blue.400, blue.600)"
                    color="white"
                    borderRadius="xl"
                    boxShadow="0 4px 20px rgba(59, 130, 246, 0.4)"
                    _hover={{
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 24px rgba(59, 130, 246, 0.5)"
                    }}
                    _active={{ transform: "translateY(0px)" }}
                    transition="all 0.2s"
                    fontWeight="600"
                  >
                    Update Profile
                  </Button>
                </HStack>
              </VStack>
            </form>
          </Box>

          {/* Account Info */}
          <Box
            w="100%" 
            maxW="md"
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <VStack spacing={6} align="start" w="100%">
              <HStack spacing={3}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bgGradient="linear(135deg, green.400, green.600)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiUser size="20px" color="white" />
                </Box>
                <Text 
                  fontSize="lg" 
                  fontWeight="700"
                  bgGradient="linear(135deg, gray.800, gray.600)"
                  bgClip="text"
                >
                  Account Information
                </Text>
              </HStack>
              
              <VStack spacing={4} align="start" w="100%">
                <Box
                  w="100%"
                  bg="rgba(34, 197, 94, 0.05)"
                  borderRadius="xl"
                  p={4}
                  border="1px solid"
                  borderColor="green.100"
                >
                  <HStack justify="space-between" w="100%">
                    <HStack spacing={2}>
                      <Text fontSize="lg">👤</Text>
                      <Text fontSize="md" color="gray.700" fontWeight="600">Full Name:</Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="700" color="green.600">
                      {userProfile ? `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || 'Not set' : 'Not available'}
                    </Text>
                  </HStack>
                </Box>
                
                <Box
                  w="100%"
                  bg="rgba(59, 130, 246, 0.05)"
                  borderRadius="xl"
                  p={4}
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <HStack justify="space-between" w="100%">
                    <HStack spacing={2}>
                      <Text fontSize="lg">📅</Text>
                      <Text fontSize="md" color="gray.700" fontWeight="600">Joined:</Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="700" color="blue.600">
                      {userProfile?.created_at ? 
                          new Date(userProfile.created_at.seconds * 1000).toLocaleDateString() : 
                          'Recently'
                        }
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </VStack>
          </Box>

          {/* Change Password section for password users */}
          {/* {userProfile?.isPasswordUser && ( */}
            <Box
              w="100%" 
              maxW="md"
              bg="rgba(255, 255, 255, 0.8)"
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              p={{ base: 6, md: 8 }}
              boxShadow="0 8px 32px rgba(0,0,0,0.12)"
              border="1px solid"
              borderColor="whiteAlpha.500"
            >
              <VStack spacing={6} align="start" w="100%">
                <HStack spacing={3}>
                  <Box
                    p={2}
                    borderRadius="lg"
                    bgGradient="linear(135deg, red.400, red.600)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <FiLock size="20px" color="white" />
                  </Box>
                  <Text 
                    fontSize="lg" 
                    fontWeight="700"
                    bgGradient="linear(135deg, gray.800, gray.600)"
                    bgClip="text"
                  >
                    Change Password
                  </Text>
                </HStack>
                <ChangePassword />
              </VStack>
            </Box>
          {/* )} */}
        </VStack>
      </Container>
    </Box>
  );
};

export default Profile;